import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Book, X, Search, ChevronDown, ChevronRight, Check, Plus, Star } from "lucide-react";
import { roleClass } from "../lib/roleBadge";
import api from "../lib/api";

const flatten = (rubrica) => {
  const seen = new Set();
  const out = [];
  (rubrica || []).forEach((cat) =>
    cat.contacts.forEach((c) => {
      const k = c.email.toLowerCase();
      if (seen.has(k)) return;
      seen.add(k);
      out.push({ ...c, category: cat.label });
    })
  );
  return out;
};

export function RecipientField({ label, hint, value, onChange, rubrica, onOpenRubrica, testId, flushRef, onSaveContact }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [pendingSave, setPendingSave] = useState(null);
  const [savingContact, setSavingContact] = useState(false);
  const ref = useRef(null);
  const [remote, setRemote] = useState([]);
  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setRemote([]); return undefined; }
    const h = setTimeout(() => {
      api.get("/studio/email-search", { params: { q: t } }).then(({ data }) => setRemote(Array.isArray(data) ? data : [])).catch(() => setRemote([]));
    }, 200);
    return () => clearTimeout(h);
  }, [q]);
  const emails = useMemo(() => new Set(value.map((v) => v.email.toLowerCase())), [value]);
  const flat = useMemo(() => flatten(rubrica), [rubrica]);

  const isInRubrica = (email) => flat.some((c) => c.email.toLowerCase() === (email || "").toLowerCase());
  const maybePrompt = (email) => {
    if (onSaveContact && email && email.includes("@") && !isInRubrica(email)) setPendingSave(email);
  };
  const confirmSave = async () => {
    if (!pendingSave) return;
    setSavingContact(true);
    try { await onSaveContact(pendingSave); } finally { setSavingContact(false); setPendingSave(null); }
  };

  // Committa l'email digitata ma non ancora confermata; restituisce l'elenco aggiornato.
  const commitPending = () => {
    const e = q.trim().replace(/[,;]+$/, "").trim();
    if (e && e.includes("@") && !emails.has(e.toLowerCase())) {
      const next = [...value, { name: "", email: e }];
      onChange(next);
      setQ("");
      return next;
    }
    return value;
  };
  if (flushRef) flushRef.current = commitPending;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    const loc = flat.filter((c) => !emails.has(c.email.toLowerCase()) && `${c.name} ${c.email} ${c.role}`.toLowerCase().includes(t));
    const have = new Set(loc.map((c) => c.email.toLowerCase()));
    const rem = remote.filter((c) => !emails.has(c.email.toLowerCase()) && !have.has(c.email.toLowerCase()));
    return [...loc, ...rem].slice(0, 12);
  }, [q, flat, emails, remote]);

  const addTag = (c) => {
    if (emails.has(c.email.toLowerCase())) return;
    onChange([...value, { name: c.name || "", email: c.email }]);
    setQ(""); setOpen(false);
  };
  const addRaw = () => {
    const e = q.trim().replace(/[,;]+$/, "").trim();
    if (e && e.includes("@") && !emails.has(e.toLowerCase())) { onChange([...value, { name: "", email: e }]); maybePrompt(e); }
    setQ(""); setOpen(false);
  };
  const removeTag = (email) => onChange(value.filter((v) => v.email !== email));

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (results[0]) addTag(results[0]); else addRaw();
    } else if (e.key === "Backspace" && !q && value.length) {
      removeTag(value[value.length - 1].email);
    }
  };

  return (
    <div ref={ref} className="relative">
      <Label className="text-slate-300">{label} {hint && <span className="text-slate-500 font-normal">{hint}</span>}</Label>
      <div className="mt-1.5 flex items-start gap-2">
        <div className="flex-1 min-h-[42px] flex flex-wrap items-center gap-1.5 rounded-md bg-slate-900/60 border border-slate-700 px-2 py-1.5 focus-within:ring-1 focus-within:ring-amber-500/40" data-testid={`${testId}-box`}>
          {value.map((t) => (
            <span key={t.email} className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs px-2 py-0.5 max-w-full" data-testid={`${testId}-tag-${t.email}`}>
              <span className="truncate">{t.name ? `${t.name} · ${t.email}` : t.email}</span>
              <button type="button" onClick={() => removeTag(t.email)} data-testid={`${testId}-remove-${t.email}`} className="text-amber-300 hover:text-amber-500 shrink-0">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => { const e = q.trim().replace(/[,;]+$/, "").trim(); commitPending(); maybePrompt(e); }}
            onKeyDown={onKeyDown}
            data-testid={testId}
            placeholder={value.length ? "" : "Cerca o scrivi un'email…"}
            className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-slate-100 placeholder:text-slate-500 py-0.5"
            autoComplete="off"
          />
        </div>
        <button type="button" onClick={onOpenRubrica} title="Apri rubrica" data-testid={`${testId}-rubrica-btn`}
          className="mt-0.5 h-[42px] w-[42px] shrink-0 flex items-center justify-center rounded-md border border-slate-700 bg-slate-900/60 text-amber-400 hover:bg-amber-500/10">
          <Book className="h-4 w-4" />
        </button>
      </div>

      {open && results.length > 0 && (
        <div className="absolute z-[90] mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-xl max-h-64 overflow-y-auto" data-testid={`${testId}-suggestions`}>
          {results.map((c) => (
            <button key={c.email} type="button" onMouseDown={(e) => { e.preventDefault(); addTag(c); }}
              data-testid={`${testId}-suggestion-${c.email}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-800/60 border-b border-slate-800 last:border-0">
              <div className="text-sm text-slate-100 truncate flex items-center gap-1">{c.name || c.email}{c.preferito && <Star className="h-3 w-3 shrink-0 text-amber-500" fill="currentColor" aria-label="Preferito" />}</div>
              <div className="text-[11px] text-slate-500 font-mono flex items-center gap-2">
                <span className="truncate">{c.email}</span>
                {c.role && <span className={`shrink-0 inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${roleClass(c.role)}`}>{c.role}</span>}
                <span className="ml-auto text-slate-600">{c.category}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {pendingSave && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-100 px-3 py-2 text-xs text-[#1a1a1a]" data-testid={`${testId}-save-prompt`}>
          <span className="flex-1 text-[#1a1a1a]">Questo contatto (<span className="font-mono text-[#1a1a1a]">{pendingSave}</span>) non è in rubrica. Vuoi salvarlo?</span>
          <button type="button" onClick={confirmSave} disabled={savingContact} data-testid={`${testId}-save-yes`}
            className="shrink-0 rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-[#1a1a1a] font-semibold px-2.5 py-1">Sì, salva</button>
          <button type="button" onClick={() => setPendingSave(null)} data-testid={`${testId}-save-no`}
            className="shrink-0 rounded-md border border-amber-400 text-[#1a1a1a] hover:bg-amber-200 px-2.5 py-1">No</button>
        </div>
      )}
    </div>
  );
}

export function RubricaDialog({ open, onClose, categories, fieldLabel, currentEmails, onAdd }) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [checked, setChecked] = useState({});
  const [localAdded, setLocalAdded] = useState(() => new Set());

  useEffect(() => {
    if (open) {
      setQ(""); setChecked({}); setLocalAdded(new Set());
      const init = {};
      (categories || []).forEach((c, i) => (init[c.key] = i < 2));
      setExpanded(init);
    }
  }, [open, categories]);

  const isAdded = (email) => currentEmails.has(email.toLowerCase()) || localAdded.has(email.toLowerCase());

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (categories || [])
      .map((cat) => ({
        ...cat,
        contacts: term
          ? cat.contacts.filter((c) => `${c.name} ${c.email} ${c.role}`.toLowerCase().includes(term))
          : cat.contacts,
      }))
      .filter((cat) => cat.contacts.length > 0);
  }, [categories, q]);

  const toggleCheck = (c) => {
    if (isAdded(c.email)) return;
    setChecked((prev) => {
      const n = { ...prev };
      if (n[c.email]) delete n[c.email]; else n[c.email] = { name: c.name, email: c.email };
      return n;
    });
  };

  const addOne = (c) => {
    if (isAdded(c.email)) return;
    onAdd([{ name: c.name, email: c.email }]);
    setLocalAdded((prev) => new Set(prev).add(c.email.toLowerCase()));
    setChecked((prev) => { const n = { ...prev }; delete n[c.email]; return n; });
  };

  const addSelected = () => {
    const list = Object.values(checked);
    if (!list.length) return;
    onAdd(list);
    setLocalAdded((prev) => { const n = new Set(prev); list.forEach((c) => n.add(c.email.toLowerCase())); return n; });
    setChecked({});
  };

  const selectedCount = Object.keys(checked).length;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="bg-popover border-border text-popover-foreground w-full sm:max-w-md p-0 flex flex-col z-[95]" data-testid="rubrica-panel">
        <SheetHeader className="px-4 pt-4 pb-3 border-b border-border">
          <SheetTitle className="font-head text-foreground flex items-center gap-2">
            <Book className="h-4 w-4 text-amber-600" /> Rubrica contatti
          </SheetTitle>
          <p className="text-xs text-muted-foreground">Aggiungi contatti al campo <span className="text-amber-600 font-medium">{fieldLabel}</span></p>
          <div className="relative mt-2">
            <Search className="h-4 w-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, email, ragione sociale…"
              data-testid="rubrica-search" className="pl-8 bg-background border-border" />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Nessun contatto trovato.</div>
          ) : filtered.map((cat) => {
            const isOpen = q.trim() ? true : !!expanded[cat.key];
            return (
              <div key={cat.key} className="rounded-lg border border-border bg-muted/30" data-testid={`rubrica-cat-${cat.key}`}>
                <button type="button" onClick={() => setExpanded((p) => ({ ...p, [cat.key]: !p[cat.key] }))}
                  data-testid={`rubrica-cat-toggle-${cat.key}`}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left">
                  {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                  <span className="text-[11px] uppercase tracking-wider font-mono text-amber-600 font-semibold flex-1">{cat.label}</span>
                  <span className="text-[10px] text-muted-foreground font-mono">{cat.contacts.length}</span>
                </button>
                {isOpen && (
                  <div className="px-2 pb-2 space-y-1">
                    {cat.contacts.map((c) => {
                      const added = isAdded(c.email);
                      return (
                        <div key={`${cat.key}-${c.email}`}
                          className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${added ? "bg-emerald-500/10 border border-emerald-500/25" : "hover:bg-accent cursor-pointer"}`}
                          data-testid={`rubrica-contact-${c.email}`}
                          onClick={() => !added && addOne(c)}>
                          <div onClick={(e) => e.stopPropagation()}>
                            <Checkbox checked={added || !!checked[c.email]} disabled={added}
                              onCheckedChange={() => toggleCheck(c)} data-testid={`rubrica-check-${c.email}`}
                              className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-foreground truncate">{c.name}</span>
                              {c.preferito && <Star className="h-3 w-3 shrink-0 text-amber-500" fill="currentColor" aria-label="Preferito" data-testid={`rubrica-pref-${c.email}`} />}
                              {c.role && (
                                <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${roleClass(c.role)}`} data-testid={`rubrica-role-${c.email}`}>
                                  {c.role}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-muted-foreground font-mono truncate">{c.email}</div>
                          </div>
                          {added && <Check className="h-4 w-4 text-emerald-500 shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="border-t border-border p-3 flex items-center gap-2">
          <button type="button" onClick={onClose} className="text-sm rounded-md border border-border text-foreground hover:bg-accent px-3 py-2">Chiudi</button>
          <button type="button" onClick={addSelected} disabled={!selectedCount} data-testid="rubrica-add-selected"
            className="flex-1 flex items-center justify-center gap-1.5 text-sm rounded-md bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-[#0a0d14] font-semibold px-3 py-2">
            <Plus className="h-4 w-4" /> Aggiungi selezionati{selectedCount ? ` (${selectedCount})` : ""}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
